import { 
  LifeBuoy, BookOpen, MessageCircle, Mail, ExternalLink, 
  FileQuestion, Video, Users, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const AdminHelp = () => {
  const helpSections = [
    {
      title: 'Getting Started',
      description: 'Learn the basics of managing your website',
      icon: Zap,
      iconBg: 'bg-blue-100 text-blue-600',
      links: [
        { label: 'Dashboard Overview', href: '#' },
        { label: 'Adding Content', href: '#' },
        { label: 'Managing Pages', href: '#' },
        { label: 'SEO Basics', href: '#' },
      ]
    },
    {
      title: 'Documentation',
      description: 'Detailed guides for all features',
      icon: BookOpen,
      iconBg: 'bg-green-100 text-green-600',
      links: [
        { label: 'Blog Management', href: '#' },
        { label: 'Service Pages', href: '#' },
        { label: 'Media Library', href: '#' },
        { label: 'Analytics Setup', href: '#' },
      ]
    },
    {
      title: 'FAQs',
      description: 'Quick answers to common questions',
      icon: FileQuestion,
      iconBg: 'bg-purple-100 text-purple-600',
      links: [
        { label: 'How to edit pages?', href: '#' },
        { label: 'How to add images?', href: '#' },
        { label: 'How to set up analytics?', href: '#' },
        { label: 'How to manage forms?', href: '#' },
      ]
    },
  ];

  const supportChannels = [
    {
      title: 'Email Support',
      description: 'Get help via email within 24 hours',
      icon: Mail,
      action: 'Send Email',
      href: 'mailto:info@segcareersupport.com'
    },
    {
      title: 'Live Chat',
      description: 'Chat with our support team',
      icon: MessageCircle,
      action: 'Start Chat',
      href: '#'
    },
    {
      title: 'Community Forum',
      description: 'Connect with other users',
      icon: Users,
      action: 'Visit Forum',
      href: '#'
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-help">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-3">
          <LifeBuoy className="w-8 h-8 text-primary" />
          Help & Support
        </h1>
        <p className="text-muted-foreground mt-1">
          Find answers, learn how to use features, and get support
        </p>
      </div>

      {/* Quick Help Search */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="py-8 text-center">
          <h2 className="text-xl font-semibold mb-2">How can we help you today?</h2>
          <p className="text-muted-foreground mb-4">
            Browse our help resources or contact support directly
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button asChild>
              <a href="mailto:info@segcareersupport.com">
                <Mail className="w-4 h-4 mr-2" />
                Contact Support
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href="https://segcareersupport.com" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Main Site
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Sections */}
      <div className="grid md:grid-cols-3 gap-6">
        {helpSections.map((section) => (
          <Card key={section.title}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${section.iconBg}`}>
                  <section.icon className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">{section.title}</CardTitle>
                  <CardDescription>{section.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <a 
                      href={link.href}
                      className="text-sm text-muted-foreground hover:text-primary hover:underline flex items-center gap-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Support Channels */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Contact Support</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {supportChannels.map((channel) => (
            <Card key={channel.title} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                    <channel.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold">{channel.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{channel.description}</p>
                  <Button variant="outline" size="sm" asChild>
                    <a href={channel.href}>
                      {channel.action}
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Keyboard Shortcuts</CardTitle>
          <CardDescription>Speed up your workflow with these shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm">Save changes</span>
              <kbd className="px-2 py-1 text-xs bg-background border rounded">Ctrl + S</kbd>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm">New blog post</span>
              <kbd className="px-2 py-1 text-xs bg-background border rounded">Ctrl + N</kbd>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm">Open search</span>
              <kbd className="px-2 py-1 text-xs bg-background border rounded">Ctrl + K</kbd>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm">Toggle sidebar</span>
              <kbd className="px-2 py-1 text-xs bg-background border rounded">Ctrl + B</kbd>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <Card className="bg-muted/30">
        <CardContent className="py-6">
          <div className="text-center">
            <p className="text-muted-foreground">
              Can't find what you're looking for?
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Email us at{' '}
              <a href="mailto:support@segcareersupport.com" className="text-primary hover:underline">
                info@segcareersupport.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHelp;
